<template>
  <div id="app" class="bg-[#fff] max-w-[800px] mx-auto relative min-h-[100vh] font-montserrat">
    <div
      v-if="pullDistance > 0 || isRefreshing"
      class="fixed -top-12 left-0 right-0 z-50 flex justify-center pointer-events-none"
    >
      <div
        class="mt-2 px-4 py-2 bg-white rounded-full shadow flex items-center gap-2 text-sm pull-indicator font-bold text-primary"
        :class="{ dragging: pulling }"
        :style="{ transform: `translateY(${pullDistance / 2}px)` }"
      >
        <span
          v-if="!isRefreshing"
          class="transition-transform"
          :style="{
            transform: isRefreshing
              ? 'rotate(360deg)'
              : pullDistance > THRESHOLD
                ? 'rotate(180deg)'
                : 'rotate(0deg)'
          }"
        >
          ↓
        </span>

        <span>{{ pullText }}</span>
      </div>
    </div>

    <router-view v-if="isReady"/>
    <div v-else class="fixed inset-0 bg-primary items-center flex justify-center z-50 text-center px-4">
      <div v-if="!errorLoader" class="flex flex-col gap-3">
        <div class="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
        <div class="text-lg font-semibold text-white">Mohon tunggu...</div>
      </div>
      <div v-else>
        <div class="text-white text-lg font-bold">Gagal memuat data</div>
        <button
          @click="retryFetch"
          class="mt-4 bg-white text-primary px-4 py-2 rounded-lg font-semibold hover:bg-red-100 transition"
        >
          Coba Lagi
        </button>
      </div>
    </div>

    <NavBar v-if="isNavBarVisible" />

    <div   
      class="fixed bg-white z-[99999] w-10/12 rounded-xl
        left-1/2 -translate-x-1/2
        p-3.5 shadow-2xl
        border border-neutral-200
        transition-transform duration-400 ease-in
        -translate-y-28 top-0 cursor-pointer"
      :class="notification ? 'animate-toast-in' : 'animate-toast-out'"
      @click="notification = false"
    >

      <div class="font-bold text-primary">{{notificationTitle}}</div>
      <div class="text-sm">{{notificationDescription}}</div>
      
      <div class="absolute bottom-0 bg-gray-300 rounded-md w-full left-0">
        <div class="h-1  overflow-hidden ">
          <div
            class="h-full bg-primary origin-left shadow-md"
            :class="notification ? 'animate-toast-progress' : ''"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { App } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';
// import { Capacitor } from '@capacitor/core';
import NavBar from './components/Nav.vue';
import Pusher from 'pusher-js';
import { useAcademicYearStore } from './stores/useAcademicYearStore';
import { useChildrenStore } from './stores/useChildrenStore';
import { useProfileStore } from './stores/useProfileStore';
import { RoleEnum } from './enums/RoleEnum';
import { useNotificationStore } from './stores/useNotificationStore';
import { useConfigStore } from './stores/useConfigStore';
import { PushNotifications } from '@capacitor/push-notifications';
import api from './services/api';
import { refreshBus } from './utils/refreshBus';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { getSchoolApiUrl, getSchoolPusherConfig, getSchoolSlug } from './services/schoolService';

const route = useRoute();
const router = useRouter();

const academicStore = useAcademicYearStore();
const childrenStore = useChildrenStore();
const profileStore = useProfileStore();
const notificationStore = useNotificationStore();
const configStore = useConfigStore();

const isReady = ref(false);
const errorLoader = ref(false);
const isBootstrapping = ref(false);

const isNavBarVisible = computed(() => {
  const hiddenRoutes = ['Login', 'Register', 'SchoolPairing', 'CloseSelf', 'OTPScreen', 'ForgotPassword', 'ChangePassword'];
  return route.name && !hiddenRoutes.includes(route.name);
});

const isParent = computed(() => {
  return profileStore.profile?.roles?.some(role => role.name === RoleEnum.PARENTS) || false;
});

const isNative = Capacitor.isNativePlatform()

const onPullRefresh = async () => {
  refreshBus.emit('refresh')
}

const pullDistance = ref(0)
const pullText = ref('Tarik untuk refresh')
const isRefreshing = ref(false)

let startY = 0
let pulling = false

const THRESHOLD = 100

const onTouchStart = (e) => {
  if (!isNative) return
  if (window.scrollY !== 0) return

  startY = e.touches[0].clientY

  pulling = true
}

const onTouchMove = (e) => {
  if (!pulling || isRefreshing.value) return
  
  const diff = e.touches[0].clientY - startY
  if (diff <= 0) return

  document.documentElement.classList.add('pulling')

  pullDistance.value = Math.min(diff, THRESHOLD + 20)

  pullText.value =
    pullDistance.value > THRESHOLD
      ? 'Lepas untuk refresh'
      : 'Tarik untuk refresh'
}

const onTouchEnd = async () => {
  if (!pulling) return
  pulling = false

  if(!pulling) {
    document.documentElement.classList.remove('pulling')
  }

  if (pullDistance.value > THRESHOLD) {
    isRefreshing.value = true
    pullText.value = 'Memuat...'

    onPullRefresh()

    // simulasi nunggu halaman fetch
    isRefreshing.value = false
    pullDistance.value = 0
    pullText.value = 'Tarik untuk refresh'
  } else {
    pullDistance.value = 0
  }
}


const checkExcludedRoute = async () => {
  if (isBootstrapping.value) return; // ⛔ cegah double call
  isBootstrapping.value = true;
  
  try {
    const excludedRoutes = ['Login', 'Register', 'SchoolPairing', 'CloseSelf', 'OTPScreen', 'ForgotPassword', 'ChangePassword'];
    if (excludedRoutes.includes(route.name)) {
      isReady.value = true;
      if (pusher.value) {
        if (chatChannel.value) {
          chatChannel.value.unbind_all();
          chatChannel.value.unsubscribe();
          chatChannel.value = null;
        }
        pusher.value.disconnect();
        pusher.value = null;
        window.globalPusher = null;
      }
      return;
    }

    // Trigger config fetch in parallel immediately so theme colors update right away
    const configPromise = configStore.fetchConfig(true);

    await academicStore.fetchAcademicYear();
    await profileStore.fetchProfile();
    await configPromise;
    
    await fetchData();
    isReady.value = true;
  } catch (err) {
    console.error('Gagal fetch awal:', err);
    errorLoader.value = true;
  }
};

const pusher = ref(null);
const chatChannel = ref(null);

const initializePusher = () => {
  if (pusher.value) return;

  const pusherConfig = getSchoolPusherConfig();
  const key = pusherConfig.key;
  const cluster = pusherConfig.cluster;
  const apiURL = getSchoolApiUrl();

  if (!key || !cluster || !apiURL) {
    console.warn('Pusher credentials missing in App.vue');
    return;
  }

  pusher.value = new Pusher(key, {
    cluster: cluster,
    forceTLS: true,
    authEndpoint: apiURL + '/broadcasting/auth',
    auth: {
      headers: {
        'Authorization': localStorage.getItem('token')
      }
    }
  });

  window.globalPusher = pusher.value;

  const schoolSlug = getSchoolSlug();
  const channelPrefix = schoolSlug ? `${schoolSlug}-` : '';
  const channelName = 'private-' + channelPrefix + 'user-chat.' + profileStore.profile.id;
  chatChannel.value = pusher.value.subscribe(channelName);

  chatChannel.value.bind('message.sent', async (data) => {
    const isChatPage = route.name === 'QAChat' || route.name === 'GroupChatRoom';
    if (!isChatPage) {
      showNotification(data.sender_name, data.message);

      try {
        await LocalNotifications.schedule({
          notifications: [
            {
              id: Math.floor(Math.random() * 100000),
              title: data.sender_name || 'Pesan Baru',
              body: data.message || '',
              actionTypeId: 'CHAT_ACTIONS',
              extra: {
                type: 'chat',
                redirect: 'QAChat',
                sender_id: data.sender_id,
                contact_id: data.sender_id,
                name: data.sender_name,
                sender_name: data.sender_name,
              },
              channelId: 'fcm_high_priority_channel',
            },
          ],
        });
      } catch (err) {
        console.error('Failed to schedule local notification for chat:', err);
      }
    }
  });
};

const fetchData = async () => {
  try {
    const profile = profileStore.profile;

    if (isParent.value) {
      await childrenStore.fetchChildrenList(profile.id);
    }

    initializePusher();

    notificationStore.fetchNotificationCount();
  } catch (err) {
    console.error('Error in fetchData:', err);
  }
};

const handleNotificationClick = async (notification) => {
  const data = notification.notification?.extra || notification.notification?.data || {};
  const actionId = notification.actionId;
  const inputValue = notification.inputValue;

  if (actionId === 'mark_as_read') {
    const contactId = data.sender_id || data.contact_id;
    if (contactId) {
      try {
        await api.post(`/chat/read/${contactId}`);
      } catch (err) {
        console.error('Failed to mark message as read from notification:', err);
      }
    }
    return;
  }

  if (actionId === 'reply' && inputValue) {
    const contactId = data.sender_id || data.contact_id;
    const isGroup = data.type === 'group_chat' || !!data.room_id;
    const roomId = data.room_id ? String(data.room_id).replace('room-', '') : '';

    try {
      if (isGroup && roomId) {
        await api.post('/chat/send', {
          room_id: 'room-' + roomId,
          message: inputValue,
          type: 'text',
        });
      } else if (contactId) {
        await api.post('/chat/send', {
          receiver_id: contactId,
          message: inputValue,
          type: 'text',
        });
      }
    } catch (err) {
      console.error('Failed to send reply from notification action:', err);
    }
    return;
  }

  const redirect = data.redirectTo || data.redirect;
  if (redirect === 'GroupChatRoom' || data.type === 'group_chat') {
    const roomId = data.room_id ? String(data.room_id).replace('room-', '') : '';
    router.push({
      name: 'GroupChatRoom',
      params: { roomId: roomId || data.room_id },
      query: { type: data.type }
    });
  } else if (redirect === 'QAChat' || data.type === 'chat') {
    router.push({
      name: 'QAChat',
      query: {
        type: data.type,
        receiver_id: data.sender_id || data.contact_id,
        name: data.name || data.sender_name,
      }
    });
  } else if (redirect === 'Notification') {
    router.push({ name: 'Notification' });
  } else if (redirect) {
    router.push({ name: redirect, query: { type: data.type } });
  }
};

const handleBackButton = () => {
  const currentPath = router.currentRoute.value.path;
  if (currentPath === '/home' && confirm('Apakah kamu ingin keluar dari aplikasi?')) {
    App.exitApp();
  } else {
    window.history.back();
  }
};

const retryFetch = async () => {
  errorLoader.value = false;
  isReady.value = false;
  await checkExcludedRoute();
  if (!isReady.value) errorLoader.value = true;
};

watch(() => route.path, (path) => {
  document.body.classList.toggle('scale-x-[-1]', path === '/scan-qr-code');
});

watch(() => route.fullPath, () => {
  checkExcludedRoute();
  
  if (window.parent !== window) {
    const path = route.path;
    if (path.includes('history') || path.includes('payment-processing') || path.includes('close-self')) {
      const amount = route.query.amount || '0';
      window.parent.postMessage({ type: 'payment_completed', amount: amount }, '*');
    }
  }
});

const initPush = async () => {
  try {
    // 1. Request permission
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') {
      console.log('Push notification permission denied');
      return;
    }

    // Create Notification Channel with importance 5 for Android
    if (Capacitor.getPlatform() === 'android') {
      await PushNotifications.createChannel({
        id: 'fcm_high_priority_channel',
        name: 'High Priority',
        description: 'Pemberitahuan Penting',
        importance: 5,
        visibility: 1,
        vibration: true,
      });

      await LocalNotifications.createChannel({
        id: 'fcm_high_priority_channel',
        name: 'High Priority',
        description: 'Pemberitahuan Penting',
        importance: 5,
        visibility: 1,
        vibration: true,
      });
    }

    // Register action types with Reply and Mark as Read buttons on Native Notification Tray
    try {
      await LocalNotifications.registerActionTypes({
        types: [
          {
            id: 'CHAT_ACTIONS',
            actions: [
              {
                id: 'reply',
                title: 'Balas',
                input: true,
                inputPlaceholder: 'Ketik balasan...',
                inputButtonTitle: 'Kirim',
              },
              {
                id: 'mark_as_read',
                title: 'Tandai Dibaca',
              },
            ],
          },
        ],
      });
    } catch (e) {
      console.warn('LocalNotifications registerActionTypes error:', e);
    }

    // 2. Pasang listener TERLEBIH DAHULU sebelum register
    await PushNotifications.removeAllListeners();

    PushNotifications.addListener('registration', async token => {
      console.log('FCM Token received:', token.value);
      try {
        await api.post('/fcm/token', {
          token: token.value,
          platform: Capacitor.getPlatform(),
        });
      } catch (err) {
        console.error('Failed to send FCM token to server:', err);
      }
    });

    PushNotifications.addListener('registrationError', err => {
      console.error('FCM Registration error:', err);
    });

    PushNotifications.addListener('pushNotificationReceived', async notif => {
      const isChatPage = route.name === 'QAChat' || route.name === 'GroupChatRoom';
      if (!isChatPage) {
        showNotification(notif.title, notif.body);

        const data = notif.data || {};
        const isChat = data.type === 'chat' || data.type === 'group_chat' || data.redirect === 'QAChat' || data.redirect === 'GroupChatRoom';

        if (isChat) {
          try {
            await LocalNotifications.schedule({
              notifications: [
                {
                  id: Math.floor(Math.random() * 100000),
                  title: notif.title || 'Pesan Baru',
                  body: notif.body || '',
                  actionTypeId: 'CHAT_ACTIONS',
                  extra: data,
                  channelId: 'fcm_high_priority_channel',
                },
              ],
            });
          } catch (err) {
            console.error('Failed to schedule native notification for chat:', err);
          }
        }
      }
    });

    PushNotifications.addListener('pushNotificationActionPerformed', async action => {
      const data = action.notification?.data || {};
      const actionId = action.actionId;

      if (actionId === 'mark_as_read') {
        const contactId = data.sender_id || data.contact_id;
        if (contactId) {
          try {
            await api.post(`/chat/read/${contactId}`);
          } catch (err) {
            console.error('Failed to mark chat as read:', err);
          }
        }
        return;
      }

      if (data?.redirect) {
        if (data.redirect === 'GroupChatRoom' || data.type === 'group_chat') {
          const roomId = data.room_id ? String(data.room_id).replace('room-', '') : '';
          router.push({
            name: 'GroupChatRoom',
            params: { roomId: roomId || data.room_id },
            query: { type: data.type }
          });
        } else if (data.redirect === 'QAChat' || data.type === 'chat') {
          router.push({
            name: 'QAChat',
            query: {
              type: data.type,
              receiver_id: data.sender_id || data.contact_id,
              name: data.name || data.sender_name,
            }
          });
        } else {
          router.push({ name: data.redirect, query: { type: data.type } });
        }
      }
    });

    // 3. Register ke FCM setelah listener terpasang
    await PushNotifications.register();
  } catch (err) {
    console.error('initPush error:', err);
  }
};

onMounted(async () => {
  App.addListener('backButton', handleBackButton);
  LocalNotifications.addListener('localNotificationActionPerformed', handleNotificationClick);

  await router.isReady(); // pastiin route udah siap
  checkExcludedRoute();

  if (isNative) {
    CapacitorUpdater.notifyAppReady().catch(err => {
      console.warn('CapacitorUpdater notifyAppReady error:', err);
    });
  }

  if (!isNative) return
  window.addEventListener('touchstart', onTouchStart, { passive: true })
  window.addEventListener('touchmove', onTouchMove, { passive: true })
  window.addEventListener('touchend', onTouchEnd)
  initPush()
});

onUnmounted(() => {
  window.removeEventListener('touchstart', onTouchStart, { passive: true })
  window.removeEventListener('touchmove', onTouchMove, { passive: true })
  window.removeEventListener('touchend', onTouchEnd)
  window.removeEventListener('force-refetch', checkExcludedRoute);
});

const notification = ref(false);
const notificationTitle = ref('');
const notificationDescription = ref('');

const showNotification = (title, body) => {
  notification.value = true
  notificationTitle.value = title
  notificationDescription.value = body
  
  setTimeout(() => {
    notification.value = false
  }, 3000)
}
</script>

<style scoped>
@keyframes toast-in {
  0% {
    transform: translate(-50%, -7rem);
  }
  70% {
    transform: translate(-50%, 1.5rem); /* translate-y-6 */
  }
  100% {
    transform: translate(-50%, 1rem); /* translate-y-4 */
  }
}

@keyframes toast-out {
  0% {
    transform: translate(-50%, 1rem);
    opacity: 1;
  }
  70% {
    transform: translate(-50%, 1.5rem); /* translate-y-6 */
  }
  100% {
    transform: translate(-50%, -7rem);
    opacity: 0;
  }
}

@keyframes toast-progress {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.animate-toast-in {
  animation: toast-in 0.4s cubic-bezier(.34,1.56,.64,1) forwards;
}

.animate-toast-out {
  animation: toast-out 0.25s ease-in forwards;
}

.animate-toast-progress {
  animation: toast-progress 3s linear forwards;
}
</style>